import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TableWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveTotal,
	languageDisplayName
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	GetSocialListeningMyBrandAnalyticsLanguagesService,
	LanguageTop
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-languages.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

interface LanguageStat {
	language: string;
	sharePct: string;
	mentions: number;
	color: string;
}

// Categorical donut / dot palette — one color per language row, in order,
// mirroring the LOLLY report mockup (Figma 13302:101905). Cycles if there are
// more rows than colors; the final swatch is the neutral "Other" grey.
const LANGUAGE_COLORS = [
	'#2872e1',
	'#f5c451',
	'#2bb673',
	'#e94f6a',
	'#a98cf0',
	'#f0a3c6',
	'#6cd6c8',
	'#9aa3bb'
];

@Component({
	standalone: true,
	selector: 'app-languages-slide',
	templateUrl: './languages-slide.component.html',
	styleUrls: ['./languages-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		DecimalPipe,
		TypographyComponent,
		IconComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class LanguagesSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	languageStats: LanguageStat[] = [];
	topSharePct = '';
	topShareLabel = '';

	doughnutData: ChartConfiguration<'doughnut'>['data'] = {
		labels: [],
		datasets: []
	};

	readonly doughnutOptions: ChartConfiguration<'doughnut'>['options'] = {
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		cutout: '58%',
		layout: {
			padding: 0
		},
		plugins: { legend: { display: false } }
	};

	readonly doughnutType = 'doughnut' as const;
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'languages',
		null,
		null
	);

	private readonly destroy$ = new Subject<void>();
	private cachedLanguages: LanguageTop[] | null = null;
	private cacheKey: string | null = null;

	constructor(
		private readonly analyticsService: GetSocialListeningMyBrandAnalyticsLanguagesService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		// Cached like every sibling slide: a change to the section itself — a
		// new chart type, an edited heading — redraws the languages already
		// here instead of asking the warehouse for the same list again.
		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedLanguages && this.cacheKey === currentCacheKey) {
			this.applyLanguages(this.cachedLanguages);
			return;
		}

		this.analyticsService
			.fetchTop(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(languages => {
				this.cachedLanguages = languages;
				this.cacheKey = currentCacheKey;
				this.applyLanguages(languages);
			});
	}

	private applyLanguages(languages: LanguageTop[]): void {
		const rawSlide = this.buildSlide(languages);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get isEmpty(): boolean {
		return (
			!this.cachedLanguages ||
			!hasPositiveTotal(
				this.cachedLanguages.map(language => language.count)
			)
		);
	}

	trackByLanguage(_: number, stat: LanguageStat): string {
		return stat.language;
	}

	private buildSlide(languages: LanguageTop[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'languages',
			title: this.item.title || 'Languages',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Languages',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				{
					id: `${prefix}_table`,
					kind: 'table',
					visible: true,
					content: {
						dataSourceKey: 'languages_table',
						columns: [
							{ key: 'language', label: 'Language' },
							{ key: 'percentage', label: 'Share' },
							{ key: 'value', label: 'Mentions' }
						],
						rows: languages.map((language, index) => ({
							language: language.language,
							percentage: language.percentage.toFixed(1),
							value: String(language.count),
							color: LANGUAGE_COLORS[
								index % LANGUAGE_COLORS.length
							]
						}))
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		this.dateLabel =
			this.getTitleWidget(slide, 'date_range', hidden)?.content.text ??
			slide.dateRange?.label ??
			'';

		this.summaryText =
			this.getSummaryWidget(slide, hidden)?.content.text ??
			slide.description ??
			'';
		this.title =
			this.getTitleWidget(slide, 'title', hidden)?.content.text ??
			slide.title;
		this.titleColor =
			this.getTitleWidget(slide, 'title', hidden)?.content.color ??
			'#2872e1';

		const table = slide.widgets.find(
			widget => widget.kind === 'table' && !hidden.has(widget.id)
		) as TableWidget | undefined;

		this.languageStats = (table?.content.rows ?? [])
			.slice(0, 10)
			.map(row => {
				const pct = parseFloat(row['percentage'] ?? '') || 0;
				return {
					language: languageDisplayName(row['language'] ?? ''),
					sharePct: `${Math.round(pct)}%`,
					mentions: parseInt(row['value'] ?? '0', 10) || 0,
					color: row['color'] ?? '#2872e1'
				};
			});

		// KPI callout — leading language's share of mention volume.
		const top = this.languageStats[0];
		if (top) {
			this.topSharePct = top.sharePct;
			this.topShareLabel = `${top.language} share`;
		} else {
			this.topSharePct = '';
			this.topShareLabel = '';
		}

		this.doughnutData = {
			labels: this.languageStats.map(stat => stat.language),
			datasets: [
				{
					data: this.languageStats.map(
						stat => parseFloat(stat.sharePct) || 0
					),
					backgroundColor: this.languageStats.map(stat => stat.color),
					borderWidth: 0
				}
			]
		};

		// The same ten languages the breakdown lists, as mention counts — the
		// doughnut's rounded shares are a drawing of those counts, not
		// numbers a bar or a card should print.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: this.languageStats.map(stat => stat.language),
				series: [
					{
						key: 'mentions',
						label: 'Mentions',
						values: this.languageStats.map(stat => stat.mentions)
					}
				],
				labelColors: this.languageStats.map(stat => stat.color),
				labelColumn: 'Language'
			})
		);
	}

	private getTitleWidget(
		slide: PreviewSlide,
		kind: TitleWidget['kind'],
		hidden: Set<string>
	): TitleWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === kind && !hidden.has(widget.id)
		) as TitleWidget | undefined;
	}

	private getSummaryWidget(
		slide: PreviewSlide,
		hidden: Set<string>
	): SummaryTextWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;
	}
}
